import { useState, useEffect } from 'react';
import {
    Table,
    DatePicker,
    Select,
    Button,
    Modal,
    Form,
    Input,
    Tag,
    message
} from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { getData, createData } from '../api/api';
import { useTranslation } from 'react-i18next';

const { RangePicker } = DatePicker;           // hozircha ishlatilmayapti, lekin qoldirdik
const { Search } = Input;

const Merchants = () => {
    const { t } = useTranslation();

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
    const [filters, setFilters] = useState({});
    const [users, setUsers] = useState([]);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isUpdateModalVisible, setIsUpdateModalVisible] = useState(false);
    const [currentRecord, setCurrentRecord] = useState(null);
    const [statusChangeRecord, setStatusChangeRecord] = useState(null);
    const [isStatusModalVisible, setIsStatusModalVisible] = useState(false);
    const [form] = Form.useForm();
    const [updateForm] = Form.useForm();

    /* ----------------------------- TABLE COLUMNS ---------------------------- */
    const columns = [
        {
            title: '№',
            key: 'index',
            width: 60,
            render: (_, __, index) => index + 1 + (pagination.current - 1) * pagination.pageSize
        },
        { title: t('merchants.name'), dataIndex: 'name', key: 'name', width: 150 },
        { title: t('merchants.phone'), dataIndex: 'phone', key: 'phone', width: 150 },
        { title: t('merchants.email'), dataIndex: 'email', key: 'email', width: 200 },
        { title: t('merchants.tin'), dataIndex: 'tin', key: 'tin', width: 120 },
        { title: t('merchants.address'), dataIndex: 'address', key: 'address', width: 250 },
        {
            title: t('merchants.status'),
            dataIndex: 'status',
            key: 'status',
            width: 100,
            render: (status) =>
                status === 0 ? (
                    <Tag color="green">{t('merchants.active')}</Tag>
                ) : (
                    <Tag color="red">{t('merchants.inactive')}</Tag>
                )
        },
        {
            title: t('merchants.createdBy'),
            dataIndex: 'creatorFullname',
            key: 'creatorFullname',
            width: 250
        },
        {
            title: t('merchants.contractedBy'),
            dataIndex: 'contractorFullname',
            key: 'contractorFullname',
            width: 250
        },
        {
            title: t('merchants.actions'),
            key: 'actions',
            width: 350  ,
            render: (_, record) => (
                <>
                    <Button size="small" onClick={() => handleUpdate(record)}>
                        {t('merchants.edit')}
                    </Button>
                    <Button
                        size="small"
                        danger={record.status === 0}
                        type={record.status === 0 ? 'primary' : 'default'}
                        onClick={() => showStatusChangeModal(record)}
                        style={{ marginLeft: 8 }}
                    >
                        {record.status === 0 ? t('merchants.deactivate') : t('merchants.activate')}
                    </Button>
                </>
            )
        }
    ];

    /* ---------------------------- DATA FETCHING ---------------------------- */
    const fetchData = async () => {
        setLoading(true);
        try {
            const res = await getData('/admin/dashboard/merchants-list-by-filter', {
                page: pagination.current - 1,
                size: pagination.pageSize,
                status: -1,
                ...filters
            });
            setData(res.data?.data?.content || []);
            setPagination((p) => ({ ...p, total: res.data?.data?.totalCount }));
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    const fetchUsers = async () => {
        try {
            const res = await getData('/admin/dashboard/user-list-by-filter', {
                page: 0,
                size: 1_000_000,
                status: -1
            });
            setUsers(res.data?.data?.content || []);
        } catch (e) {
            console.error(e);
        }
    };

    useEffect(() => {
        fetchData();
    }, [pagination.current, pagination.pageSize, filters]);

    /* ------------------------------ HANDLERS ------------------------------ */
    const handleTableChange = (pag) => setPagination(pag);

    const handleFilter = (vals) => {
        setFilters(vals);
        setPagination((p) => ({ ...p, current: 1 }));
    };

    const showModal = () => {
        setIsModalVisible(true);
        fetchUsers();
    };
    const handleCancel = () => {
        setIsModalVisible(false);
        form.resetFields();
    };

    const handleCreate = async (values) => {
        try {
            const res = await createData('/admin/merchant/create', values);
            if (res?.data?.success) {
                message.success(t('merchants.successAdd'));
                fetchData();
                handleCancel();
            } else message.error(res?.data?.error?.text || t('merchants.errorOccurred'));
        } catch {
            message.error(t('merchants.errorOccurred'));
        }
    };

    const handleUpdate = (rec) => {
        setCurrentRecord(rec);
        updateForm.setFieldsValue({
            name: rec.name,
            tin: rec.tin,
            phone: rec.phone,
            email: rec.email,
            address: rec.address
        });
        setIsUpdateModalVisible(true);
    };
    const handleUpdateCancel = () => {
        setIsUpdateModalVisible(false);
        updateForm.resetFields();
        setCurrentRecord(null);
    };
    const handleUpdateSubmit = async (values) => {
        try {
            const res = await createData('/admin/merchant/update', { ...values, id: currentRecord.id });
            if (res?.data?.success) {
                message.success(t('merchants.successUpdate'));
                fetchData();
                handleUpdateCancel();
            } else message.error(res?.data?.error?.text || t('merchants.updateError'));
        } catch {
            message.error(t('merchants.updateError'));
        }
    };

    const showStatusChangeModal = (rec) => {
        setStatusChangeRecord(rec);
        setIsStatusModalVisible(true);
    };
    const handleStatusChangeCancel = () => {
        setIsStatusModalVisible(false);
        setStatusChangeRecord(null);
    };
    const handleStatusChange = async () => {
        try {
            const newStatus = statusChangeRecord.status === 0 ? 1 : 0;
            const res = await createData('/admin/merchant/update', {
                ...statusChangeRecord,
                status: newStatus
            });
            if (res?.data?.success) {
                message.success(t('merchants.statusChanged'));
                fetchData();
                handleStatusChangeCancel();
            } else message.error(res?.data?.error?.text || t('merchants.statusChangeError'));
        } catch {
            message.error(t('merchants.statusChangeError'));
        }
    };

    /* -------------------------------- RENDER ------------------------------- */
    return (
        <div>
            {/* Header & search */}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                <div style={{ display: 'flex', gap: 16 }}>
                    <h2 style={{ margin: 0 }}>{t('merchants.title')}</h2>
                    <Search
                        placeholder={t('merchants.searchPlaceholder')}
                        allowClear
                        enterButton={<SearchOutlined />}
                        size="middle"
                        style={{ width: 300 }}
                        onSearch={(val) => handleFilter({ ...filters, search: val })}
                    />
                </div>
                <Button type="primary" onClick={showModal}>
                    {t('merchants.add')}
                </Button>
            </div>

            {/* Table */}
            <Table
                columns={columns}
                dataSource={data}
                pagination={{
                    ...pagination,
                    showTotal: (total) => t('merchants.total', { total })
                }}
                loading={loading}
                onChange={handleTableChange}
                rowKey="id"
                scroll={{ x: 'max-content' }}
            />

            {/* Create modal */}
            <Modal title={t('merchants.addTitle')} open={isModalVisible} onCancel={handleCancel} footer={null}>
                <Form form={form} onFinish={handleCreate} layout="vertical">
                    <Form.Item
                        name="name"
                        label={t('merchants.name')}
                        rules={[{ required: true, message: t('merchants.nameRequired') }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        name="tin"
                        label={t('merchants.tin')}
                        rules={[{ required: true, message: t('merchants.tinRequired') }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        name="phone"
                        label={t('merchants.phone')}
                        rules={[{ required: true, message: t('merchants.phoneRequired') }]}
                    >
                        <Input placeholder="998901234567" />
                    </Form.Item>
                    <Form.Item name="email" label={t('merchants.email')}>
                        <Input placeholder="example@mail.com" />
                    </Form.Item>
                    <Form.Item name="address" label={t('merchants.address')}>
                        <Input.TextArea placeholder={t('merchants.addressPlaceholder')} />
                    </Form.Item>
                    <Form.Item
                        name="contractBy"
                        label={t('merchants.contractBy')}
                        rules={[{ required: true, message: t('merchants.contractByRequired') }]}
                    >
                        <Select
                            placeholder={t('merchants.contractBy')}
                            showSearch
                            filterOption={(input, option) =>
                                option?.children?.toString().toLowerCase().includes(input.toLowerCase())
                            }
                            allowClear
                            notFoundContent={t('merchants.notFound')}
                        >
                            {users.map((u) => (
                                <Select.Option key={u.id} value={u.id}>
                                    {u.fullname} - {u.phone}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item>
                        <Button type="primary" htmlType="submit">
                            {t('merchants.save')}
                        </Button>
                        <Button style={{ marginLeft: 8 }} onClick={handleCancel}>
                            {t('merchants.cancel')}
                        </Button>
                    </Form.Item>
                </Form>
            </Modal>

            {/* Update modal */}
            <Modal title={t('merchants.updateTitle')} open={isUpdateModalVisible} onCancel={handleUpdateCancel} footer={null}>
                <Form form={updateForm} onFinish={handleUpdateSubmit} layout="vertical">
                    <Form.Item
                        name="name"
                        label={t('merchants.name')}
                        rules={[{ required: true, message: t('merchants.nameRequired') }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        name="phone"
                        label={t('merchants.phone')}
                        rules={[{ required: true, message: t('merchants.phoneRequired') }]}
                    >
                        <Input placeholder="998901234567" />
                    </Form.Item>
                    <Form.Item
                        name="tin"
                        label={t('merchants.tin')}
                        rules={[{ required: true, message: t('merchants.tinRequired') }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item name="email" label={t('merchants.email')}>
                        <Input placeholder="example@mail.com" />
                    </Form.Item>
                    <Form.Item name="address" label={t('merchants.address')}>
                        <Input.TextArea placeholder={t('merchants.addressPlaceholder')} />
                    </Form.Item>
                    <Form.Item>
                        <Button type="primary" htmlType="submit">
                            {t('merchants.save')}
                        </Button>
                        <Button style={{ marginLeft: 8 }} onClick={handleUpdateCancel}>
                            {t('merchants.cancel')}
                        </Button>
                    </Form.Item>
                </Form>
            </Modal>

            {/* Status modal */}
            <Modal
                title={t('merchants.statusTitle')}
                open={isStatusModalVisible}
                onCancel={handleStatusChangeCancel}
                footer={[
                    <Button key="cancel" onClick={handleStatusChangeCancel}>
                        {t('merchants.cancel')}
                    </Button>,
                    <Button key="submit" type="primary" onClick={handleStatusChange}>
                        {t('merchants.confirm')}
                    </Button>
                ]}
            >
                <p>
                    {t('merchants.statusQuestion', {
                        name: statusChangeRecord?.name,
                        status:
                            statusChangeRecord?.status === 0
                                ? t('merchants.inactive')
                                : t('merchants.active')
                    })}
                </p>
            </Modal>
        </div>
    );
};

export default Merchants;
